import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { IPaginationData } from '../../../api/base';
import moment from 'moment';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SvgIcon,
  Tab,
  Tabs,
  TextField,
  Typography,
  Collapse,
} from '@mui/material';
import {
  Delete,
  Search,
  FilterList,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import theme from '../../../theme';
import { ETrackType } from '../../../models/faq.model';
import _ from 'lodash';
import { IContactSubmission } from '../../../models/contact-submission';
import contactSubmissionApi, {
  ESubmissionStatus,
} from '../../../api/contact-submission.api';
import { mdiEyeOutline, mdiCheckCircle, mdiCloseCircle } from '@mdi/js';
import { InfoTable } from './styled';
import { getLink } from '../../../utils/link';
import { getFileName } from '../../../utils/common';
import { ISubmissionAnswer } from '../../../models/contact-submission';

interface QuestionAnswerListProps {
  questions: ISubmissionAnswer[];
}

function QuestionAnswerList({ questions }: QuestionAnswerListProps) {
  // Group questions by parent
  const parentQuestions = questions.filter((q) => !q.question_parent_id);
  const childQuestionsByParent = questions.reduce((acc, q) => {
    if (q.question_parent_id) {
      if (!acc[q.question_parent_id]) {
        acc[q.question_parent_id] = [];
      }
      acc[q.question_parent_id].push(q);
    }
    return acc;
  }, {} as Record<string, ISubmissionAnswer[]>);

  // Function to detect if answer is an array and parse it
  const parseAnswerValue = (answerValue: string) => {
    if (!answerValue) return null;

    // Try to parse as JSON array
    console.log(answerValue);
    try {
      if (Array.isArray(answerValue)) {
        return answerValue;
      }
    } catch {
      // Not JSON, try comma-separated values
      console.log('error when parsing');
      console.log(answerValue);
      if (answerValue.includes(',')) {
        const commaSeparated = answerValue
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        console.log(commaSeparated);
        if (commaSeparated.length > 1) {
          return commaSeparated;
        }
      }
    }

    return null;
  };

  const renderAnswerValue = (answerValue: string) => {
    const arrayValues = parseAnswerValue(answerValue);

    // If it's an array, show each value as a tag
    if (arrayValues) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {arrayValues.map((value, index) => (
            <Chip
              key={index}
              label={String(value)}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: 12 }}
            />
          ))}
        </Box>
      );
    }

    // For single values, show as regular text
    return answerValue || 'No answer provided';
  };

  const renderQuestion = (question: ISubmissionAnswer, isChild = false) => (
    <Box key={question.question_id} sx={{ mb: 2, ml: isChild ? 3 : 0 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.primary"
        sx={{ mb: 1 }}
      >
        {isChild && '└ '}
        {question.question_title}
      </Typography>
      <Box
        sx={{
          p: 2,
          backgroundColor: isChild ? 'grey.25' : 'grey.50',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          fontSize: 14,
        }}
      >
        {renderAnswerValue(question.answer_value)}
      </Box>
    </Box>
  );

  return (
    <Box>
      {parentQuestions.map((parentQ) => (
        <Box key={parentQ.question_id} sx={{ mb: 3 }}>
          {renderQuestion(parentQ)}
          {childQuestionsByParent[parentQ.question_id]?.map((childQ) =>
            renderQuestion(childQ, true),
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function ContactSubmissionListPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [dataSource, setDataSource] = useState<
    IPaginationData<IContactSubmission>
  >({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['created_at', 'desc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
    status: undefined as ESubmissionStatus | undefined,
    track: undefined as ETrackType | undefined,
    email: '',
  });

  const [debouncedEmailFilter, setDebouncedEmailFilter] = useState('');

  const [currItem, setCurrItem] = useState<IContactSubmission | undefined>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [currApprovalItem, setCurrApprovalItem] = useState<
    IContactSubmission | undefined
  >();
  const [currRejectionItem, setCurrRejectionItem] = useState<
    IContactSubmission | undefined
  >();

  const loadData = useCallback(() => {
    setLoading(true);

    // Build query based on active tab
    let statusFilter = filter.status;
    if (activeTab !== 'all') {
      statusFilter = activeTab as ESubmissionStatus;
    }

    contactSubmissionApi
      .getList({
        keyword: filter.keyword,
        page: filter.page,
        limit: filter.limit,
        track: filter.track,
        email: debouncedEmailFilter || undefined,
        status: statusFilter,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    order,
    orderBy,
    filter.keyword,
    filter.page,
    filter.limit,
    filter.track,
    filter.status,
    activeTab,
    debouncedEmailFilter,
  ]);

  useEffect(loadData, [loadData]);

  // Debounce email filter to avoid API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmailFilter(filter.email);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [filter.email]);

  const loadSubmissionDetail = useCallback((id: string) => {
    setDetailLoading(true);
    contactSubmissionApi
      .getDetail(id)
      .then((res) => {
        setCurrItem(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setDetailLoading(false);
      });
  }, []);

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          Contact Form Submissions
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search submissions..."
              variant="outlined"
              size="small"
              name="search"
              sx={{ minWidth: 250 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton type="submit" size="small">
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ textTransform: 'none' }}
          >
            Filters {showFilters ? <ExpandLess /> : <ExpandMore />}
          </Button>
        </Box>
      </Box>

      {/* Advanced Filters */}
      <Collapse in={showFilters}>
        <Paper
          elevation={0}
          sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filter.status || ''}
                  label="Status"
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      status:
                        (e.target.value as ESubmissionStatus) || undefined,
                      page: 1,
                    })
                  }
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value={ESubmissionStatus.Pending}>Pending</MenuItem>
                  <MenuItem value={ESubmissionStatus.Approved}>
                    Approved
                  </MenuItem>
                  <MenuItem value={ESubmissionStatus.Rejected}>
                    Rejected
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Track Type</InputLabel>
                <Select
                  value={filter.track || ''}
                  label="Track Type"
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      track: (e.target.value as ETrackType) || undefined,
                      page: 1,
                    })
                  }
                >
                  <MenuItem value="">All Tracks</MenuItem>
                  <MenuItem value={ETrackType.Investor}>Investor</MenuItem>
                  <MenuItem value={ETrackType.Startup}>Startup</MenuItem>
                  <MenuItem value={ETrackType.Researcher}>Researcher</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Filter by Email"
                size="small"
                fullWidth
                value={filter.email}
                onChange={(e) =>
                  setFilter({ ...filter, email: e.target.value, page: 1 })
                }
                placeholder="Enter email address..."
              />
            </Grid>
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              size="small"
              onClick={() =>
                setFilter({
                  keyword: '',
                  page: 1,
                  limit: 10,
                  status: undefined,
                  track: undefined,
                  email: '',
                })
              }
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          aria-label="submission status tabs"
        >
          <Tab label="All" value="all" />
          <Tab label="Pending" value={ESubmissionStatus.Pending} />
          <Tab label="Approved" value={ESubmissionStatus.Approved} />
          <Tab label="Rejected" value={ESubmissionStatus.Rejected} />
        </Tabs>
      </Box>
      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        // onClick={setCurrItem}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'full_name',
            title: 'Full Name',
            dataKey: 'full_name',
            render: (data) => (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {data.full_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.organization}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'email',
            title: 'Email',
            dataKey: 'email',
            render: (data) => (
              <Box>
                <Typography variant="body2">
                  <a
                    href={`mailto:${data.email}`}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {data.email}
                  </a>
                </Typography>
                {data.phone && (
                  <Typography variant="caption" color="text.secondary">
                    {data.phone}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            key: 'track',
            title: 'Track',
            dataKey: 'track',
            render: (data) => (
              <Chip
                label={(data.track && _.words(data.track).join(' ')) || 'N/A'}
                size="small"
                color={
                  data.track === ETrackType.Investor
                    ? 'primary'
                    : data.track === ETrackType.Startup
                    ? 'secondary'
                    : 'default'
                }
                variant="outlined"
              />
            ),
          },
          {
            key: 'status',
            title: 'Status',
            dataKey: 'status',
            render: (data) => (
              <Chip
                label={
                  data.status?.charAt(0).toUpperCase() +
                    data.status?.slice(1) || 'Pending'
                }
                size="small"
                color={
                  data.status === ESubmissionStatus.Approved
                    ? 'success'
                    : data.status === ESubmissionStatus.Rejected
                    ? 'error'
                    : 'warning'
                }
              />
            ),
          },
          {
            key: 'message',
            title: 'Message',
            dataKey: 'message',
            render: (data) => (
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {data.message || 'No message'}
              </Typography>
            ),
          },
          {
            key: 'created_at',
            title: 'Submitted',
            dataKey: 'created_at',
            sortable: true,
            render: (data) => (
              <Box>
                <Typography variant="body2">
                  {moment(data.created_at).format('MMM DD, YYYY')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {moment(data.created_at).format('HH:mm')}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <Box display="flex" gap={0.5}>
                <IconButton
                  size="small"
                  onClick={() => loadSubmissionDetail(data.id)}
                  title="View Details"
                >
                  <SvgIcon fontSize="small">
                    <path d={mdiEyeOutline} />
                  </SvgIcon>
                </IconButton>
                {data.status === ESubmissionStatus.Pending && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCurrApprovalItem(data);
                        setShowApprovalModal(true);
                      }}
                      title="Approve"
                      color="success"
                    >
                      <SvgIcon fontSize="small">
                        <path d={mdiCheckCircle} />
                      </SvgIcon>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCurrRejectionItem(data);
                        setShowRejectionModal(true);
                      }}
                      title="Reject"
                      color="error"
                    >
                      <SvgIcon fontSize="small">
                        <path d={mdiCloseCircle} />
                      </SvgIcon>
                    </IconButton>
                  </>
                )}
                <IconButton
                  size="small"
                  onClick={() => setCurrDeletedId(data.id)}
                  title="Delete"
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this submission?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                contactSubmissionApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete submission successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!currItem}
        onClose={() => setCurrItem(undefined)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              Submission Details - {currItem?.full_name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 600,
                }}
              >
                {currItem?.track && _.words(currItem.track).join(' ')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setCurrItem(undefined)}
                sx={{ ml: 1 }}
              >
                <SvgIcon>
                  <path d="M19,6.41L17.59,5 12,10.59 6.41,5 5,6.41 10.59,12 5,17.59 6.41,19 12,13.41 17.59,19 19,17.59 13.41,12 19,6.41Z" />
                </SvgIcon>
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Contact Information
            </Typography>

            <InfoTable>
              <tbody>
                <tr>
                  <td className="field-label">Full Name:</td>
                  <td className="field-value">{currItem?.full_name}</td>
                </tr>
                <tr>
                  <td className="field-label">Email:</td>
                  <td className="field-value">
                    <a
                      href={`mailto:${currItem?.email}`}
                      style={{ color: theme.palette.primary.main }}
                    >
                      {currItem?.email}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className="field-label">Organization:</td>
                  <td className="field-value">{currItem?.organization}</td>
                </tr>
                <tr>
                  <td className="field-label">Track Type:</td>
                  <td className="field-value">
                    {currItem?.track && _.words(currItem.track).join(' ')}
                  </td>
                </tr>
              </tbody>
            </InfoTable>

            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, mt: 3, mb: 2 }}
            >
              Submission Details
            </Typography>

            <InfoTable>
              <tbody>
                <tr>
                  <td className="field-label">Submitted At:</td>
                  <td className="field-value">
                    {currItem
                      ? moment(currItem.created_at).format(
                          'MMMM DD, YYYY [at] HH:mm:ss',
                        )
                      : '-'}
                  </td>
                </tr>
                <tr>
                  <td className="field-label">Last Updated:</td>
                  <td className="field-value">
                    {currItem
                      ? moment(currItem.updated_at).format(
                          'MMMM DD, YYYY [at] HH:mm:ss',
                        )
                      : '-'}
                  </td>
                </tr>
                <tr>
                  <td className="field-label">Status:</td>
                  <td className="field-value">
                    <Chip
                      label={
                        currItem?.status
                          ? currItem.status.charAt(0).toUpperCase() +
                            currItem.status.slice(1)
                          : 'Pending'
                      }
                      size="small"
                      color={
                        currItem?.status === ESubmissionStatus.Approved
                          ? 'success'
                          : currItem?.status === ESubmissionStatus.Rejected
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </td>
                </tr>
                {currItem?.review_notes &&
                  (currItem.status === ESubmissionStatus.Approved ||
                    currItem.status === ESubmissionStatus.Rejected) && (
                    <tr>
                      <td className="field-label">Review Notes:</td>
                      <td
                        className="field-value"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {currItem.review_notes}
                      </td>
                    </tr>
                  )}
                {currItem?.attachment_url && (
                  <tr>
                    <td className="field-label">Attachment:</td>
                    <td className="field-value">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (currItem.attachment_url) {
                            window.open(
                              getLink(currItem.attachment_url),
                              '_blank',
                            );
                          }
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        📎 Download {getFileName(currItem.attachment_url)}
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </InfoTable>

            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, mt: 3, mb: 2 }}
            >
              Message
            </Typography>

            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
                minHeight: 100,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {currItem?.message || 'No message provided'}
            </Box>

            {(currItem?.answers || currItem?.questions) && (
              <>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mt: 3, mb: 2 }}
                >
                  Questions & Answers
                </Typography>

                <Box sx={{ mt: 2 }}>
                  {detailLoading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <QuestionAnswerList
                      questions={currItem?.answers || currItem?.questions || []}
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (currItem?.email) {
                window.open(
                  `mailto:${currItem.email}?subject=Re: Your submission&body=Dear ${currItem.full_name},\n\n`,
                  '_blank',
                );
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            📧 Reply via Email
          </Button>
          <Button
            variant="contained"
            onClick={() => setCurrItem(undefined)}
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Modal */}
      <Dialog
        open={showApprovalModal}
        onClose={() => {
          setShowApprovalModal(false);
          setApprovalNotes('');
          setCurrApprovalItem(undefined);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Approve Submission</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            You are about to approve the submission from{' '}
            <strong>{currApprovalItem?.full_name}</strong>.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Approval Notes (Optional)"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Add any notes for this approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowApprovalModal(false);
              setApprovalNotes('');
              setCurrApprovalItem(undefined);
            }}
            disabled={approving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (currApprovalItem) {
                setApproving(true);
                contactSubmissionApi
                  .approve(currApprovalItem.id, {
                    status: 'approved',
                    review_notes: approvalNotes || undefined,
                  })
                  .then(() => {
                    toast.success('Submission approved successfully');
                    loadData();
                    setShowApprovalModal(false);
                    setApprovalNotes('');
                    setCurrApprovalItem(undefined);
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setApproving(false);
                  });
              }
            }}
            variant="contained"
            color="success"
            disabled={approving}
          >
            {approving ? <CircularProgress size={20} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Modal */}
      <Dialog
        open={showRejectionModal}
        onClose={() => {
          setShowRejectionModal(false);
          setRejectionNotes('');
          setCurrRejectionItem(undefined);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Reject Submission</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            You are about to reject the submission from{' '}
            <strong>{currRejectionItem?.full_name}</strong>.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason *"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={rejectionNotes}
            onChange={(e) => setRejectionNotes(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            required
            error={showRejectionModal && !rejectionNotes.trim()}
            helperText={
              showRejectionModal && !rejectionNotes.trim()
                ? 'Rejection reason is required'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRejectionModal(false);
              setRejectionNotes('');
              setCurrRejectionItem(undefined);
            }}
            disabled={rejecting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (currRejectionItem && rejectionNotes.trim()) {
                setRejecting(true);
                contactSubmissionApi
                  .reject(currRejectionItem.id, {
                    status: 'rejected',
                    review_notes: rejectionNotes,
                  })
                  .then(() => {
                    toast.success('Submission rejected');
                    loadData();
                    setShowRejectionModal(false);
                    setRejectionNotes('');
                    setCurrRejectionItem(undefined);
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setRejecting(false);
                  });
              }
            }}
            variant="contained"
            color="error"
            disabled={rejecting || !rejectionNotes.trim()}
          >
            {rejecting ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
